import { ref, computed } from 'vue'
import { supabase, getUserProfile, updateUserProfile } from '@/lib/supabase'

// Reactive state
const user = ref(null)
const profile = ref(null)
const loading = ref(true)
const error = ref(null)

// Computed properties
const isAuthenticated = computed(() => !!user.value)
const userPlan = computed(() => profile.value?.plan || 'free')
const isPremium = computed(() => ['premium', 'lifetime'].includes(userPlan.value))

// Guard flag to prevent calling getSession more than once
let isInitializing = false
let authListenerRegistered = false
let refreshPromise = null

const syncSessionToStorage = (session) => {
  if (session?.access_token) {
    localStorage.setItem('authToken', session.access_token)
    localStorage.setItem('adminUser', session.user.email)
  }
}

const clearStoredAuth = () => {
  localStorage.removeItem('authToken')
  localStorage.removeItem('adminUser')
  localStorage.removeItem('userPlan')
  user.value = null
  profile.value = null
}

// Keep localStorage in sync when Supabase refreshes the JWT
const setupAuthSync = () => {
  if (!supabase || authListenerRegistered) return
  authListenerRegistered = true

  supabase.auth.onAuthStateChange((event, session) => {
    if (session?.access_token) {
      user.value = session.user
      syncSessionToStorage(session)
      refreshPromise = null

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        getUserProfile(session.user.id).then((p) => {
          if (p) {
            profile.value = p
            localStorage.setItem('adminUser', p.username || session.user.email)
            localStorage.setItem('userPlan', p.plan || 'free')
          }
        }).catch(err => {
          console.error('Profile fetch error:', err)
        })
      }
    } else if (event === 'SIGNED_OUT') {
      clearStoredAuth()
    }
  })
}

// Return current access token — Supabase autoRefreshToken handles renewal in background
const getAccessToken = async () => {
  if (!supabase) {
    return localStorage.getItem('authToken')
  }

  const { data: { session } } = await supabase.auth.getSession()
  if (!session) {
    const legacyToken = localStorage.getItem('authToken')
    if (legacyToken?.startsWith('auth_')) {
      return legacyToken
    }
    return null
  }

  const expiresAt = session.expires_at * 1000
  if (Date.now() < expiresAt) {
    syncSessionToStorage(session)
    return session.access_token
  }

  // Access token expired — one shared refresh for all concurrent requests
  if (!refreshPromise) {
    refreshPromise = supabase.auth.refreshSession()
      .then(({ data: { session: refreshed }, error }) => {
        refreshPromise = null
        if (error || !refreshed?.access_token) {
          clearStoredAuth()
          return null
        }
        syncSessionToStorage(refreshed)
        return refreshed.access_token
      })
      .catch(() => {
        refreshPromise = null
        clearStoredAuth()
        return null
      })
  }

  return refreshPromise
}

// Initialize auth — check for an existing session
const initAuth = async () => {
  if (!supabase || isInitializing) {
    loading.value = false
    return
  }

  isInitializing = true
  setupAuthSync()

  try {
    const { data: { session } } = await supabase.auth.getSession()

    if (session?.user) {
      user.value = session.user
      syncSessionToStorage(session)
      localStorage.setItem('userPlan', 'free')

      getUserProfile(session.user.id).then((p) => {
        if (p) {
          profile.value = p
          localStorage.setItem('adminUser', p.username || session.user.email)
          localStorage.setItem('userPlan', p.plan || 'free')
        }
      }).catch(err => {
        console.error('Profile fetch error:', err)
      })
    } else {
      // Stale legacy token with no Supabase session — clear it
      const token = localStorage.getItem('authToken')
      if (token && !token.startsWith('auth_')) {
        clearStoredAuth()
      }
    }
  } catch (err) {
    console.error('Auth init error:', err)
    error.value = err.message
  } finally {
    loading.value = false
    isInitializing = false
  }
}

// Sign in with Google
const signInWithGoogle = async () => {
  if (!supabase) {
    throw new Error('Supabase not configured')
  }

  error.value = null
  
  const { data, error: signInError } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })

  if (signInError) {
    error.value = signInError.message
    throw signInError
  }

  return data
}

// Sign out
const signOut = async () => {
  if (!supabase) return

  const { error: signOutError } = await supabase.auth.signOut()
  
  if (signOutError) {
    error.value = signOutError.message
    throw signOutError
  }

  clearStoredAuth()
}

// Update user subscription plan
const updatePlan = async (newPlan, expiresAt = null) => {
  if (!supabase || !user.value) return

  const updates = { plan: newPlan }
  if (expiresAt) {
    updates.plan_expires_at = expiresAt
  }

  const updatedProfile = await updateUserProfile(user.value.id, updates)
  if (updatedProfile) {
    profile.value = updatedProfile
    localStorage.setItem('userPlan', newPlan)
  }
  
  return updatedProfile
}

// Export composable
export { initAuth, setupAuthSync, getAccessToken }

export const useAuth = () => {
  return {
    // State
    user,
    profile,
    loading,
    error,

    // Computed
    isAuthenticated,
    userPlan,
    isPremium,

    // Methods
    initAuth,
    setupAuthSync,
    getAccessToken,
    signInWithGoogle,
    signOut,
    updatePlan
  }
}
